import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const SESSION_KEY = 'admin_in_tenant_view';
const SESSION_USER_KEY = 'tenant_view_user_id';
const SESSION_APT_KEY = 'tenant_view_apartment_id';
const APT_CACHE_PREFIX = 'view_toggle_has_apt_';

function clearAllTenantViewStorage() {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_USER_KEY);
  sessionStorage.removeItem(SESSION_APT_KEY);
  Object.keys(sessionStorage)
    .filter(k => k.startsWith(APT_CACHE_PREFIX))
    .forEach(k => sessionStorage.removeItem(k));
}

interface TenantViewContextValue {
  inTenantView: boolean;
  /** Non-null when owner/concierge is viewing a specific unit as tenant */
  viewingApartmentId: string | null;
  enterTenantView: () => void;
  /** Enter tenant view scoped to a specific apartment (from unit detail page) */
  enterTenantViewAs: (apartmentId: string) => void;
  exitTenantView: () => void;
}

const TenantViewContext = createContext<TenantViewContextValue | null>(null);

export const TenantViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [inTenantView, setInTenantView] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === 'true'
  );
  const [viewingApartmentId, setViewingApartmentId] = useState<string | null>(
    () => sessionStorage.getItem(SESSION_APT_KEY) || null
  );

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // Clear on logout: prevents stale state bleeding into the next user's session.
        clearAllTenantViewStorage();
        setInTenantView(false);
      } else if (event === 'SIGNED_IN') {
        // Clear on fresh sign-in (email/password): the new user starts outside tenant view.
        // This covers the case where stale sessionStorage was left by a prior user on the
        // same tab and the new user signs in interactively.
        clearAllTenantViewStorage();
        setInTenantView(false);
      } else if (event === 'INITIAL_SESSION' && session) {
        // INITIAL_SESSION fires when the app loads with an already-valid session (no active
        // sign-in) — e.g. an org admin who is already logged in opens the app on a tab that
        // has stale admin_in_tenant_view=true from a previous superadmin session.
        //
        // Compare the current user's ID against who originally set the tenant-view flag.
        // If they differ, wipe the stale state. If they match (page refresh), keep it.
        const storedUserId = sessionStorage.getItem(SESSION_USER_KEY);
        if (storedUserId && storedUserId !== session.user.id) {
          clearAllTenantViewStorage();
          setInTenantView(false);
          setViewingApartmentId(null);
        }
        // No storedUserId → backward compat: trust sessionStorage as-is.
        // TOKEN_REFRESHED → no change (same user, same session).
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const enterTenantView = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    sessionStorage.removeItem(SESSION_APT_KEY);
    setInTenantView(true);
    setViewingApartmentId(null);
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.id) {
        sessionStorage.setItem(SESSION_USER_KEY, session.user.id);
      }
    });
  }, []);

  const enterTenantViewAs = useCallback((apartmentId: string) => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    sessionStorage.setItem(SESSION_APT_KEY, apartmentId);
    setInTenantView(true);
    setViewingApartmentId(apartmentId);
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.id) {
        sessionStorage.setItem(SESSION_USER_KEY, session.user.id);
      }
    });
  }, []);

  const exitTenantView = useCallback(() => {
    clearAllTenantViewStorage();
    setInTenantView(false);
    setViewingApartmentId(null);
  }, []);

  return (
    <TenantViewContext.Provider value={{ inTenantView, viewingApartmentId, enterTenantView, enterTenantViewAs, exitTenantView }}>
      {children}
    </TenantViewContext.Provider>
  );
};

export const useTenantView = (): TenantViewContextValue => {
  const ctx = useContext(TenantViewContext);
  if (!ctx) throw new Error('useTenantView must be used within TenantViewProvider');
  return ctx;
};
